import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Card, message, Skeleton } from 'antd';
import { setPackingSupplies, setAlaCarteStep } from './store/actionCreators';
import { getSelectPackings } from '../../data/API';

const PackingSupplies = (props) => {
  const dispatch = useDispatch();
  const { packingSupplies, currentStep } = useSelector(state => state.alaCarte);
  const { isParentNextStepClicked, setParentNextStepClicked, tabref } = props;

  const [supplies, setSupplies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPackingSupplies();
  }, []);

  useEffect(() => {
    if (tabref?.current) {
      window.scrollTo({
        top: tabref.current.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  }, [tabref]);

  useEffect(() => {
    if (isParentNextStepClicked) {
      setParentNextStepClicked(false);
      handleNext();
    }
  }, [isParentNextStepClicked]);

  // Sync local state with Redux when Redux state changes (e.g., when items are removed from OrderSummary)
  useEffect(() => {
    if (supplies.length > 0) {
      const updatedSupplies = supplies.map(item => {
        const reduxItem = packingSupplies.find(p => p.id === item.id);
        if (reduxItem) {
          return {
            ...item,
            quantity: reduxItem.quantity,
            selectedQuantity: reduxItem.quantity
          };
        } else {
          return {
            ...item,
            quantity: 0,
            selectedQuantity: 0
          };
        }
      });
      setSupplies(updatedSupplies);
    }
  }, [packingSupplies]);


  const fetchPackingSupplies = async () => {
    try {
      setLoading(true);
      const response = await getSelectPackings('Home', 'Packing Supplies');

      if (response?.data?.results) {
        const formattedSupplies = response.data.results.map(item => {
          // Check if this item exists in Redux state
          const existing = packingSupplies.find(p => p.id === item.id);
          return {
            id: item.id,
            title: item.title,
            price: parseFloat(item.price),
            quantity: existing ? existing.quantity : 0,
            selectedQuantity: existing ? existing.quantity : 0,
            image: item.image,
            description: item.description ? item.description
              .replaceAll('</li>', ',')
              .replaceAll('&nbsp;', '')
              .replaceAll('\r\n', '')
              .replaceAll(/<\/?[^>]+(>|$)/g, '')
              .split(',')
              .filter(d => d.trim()) : []
          };
        });
        setSupplies(formattedSupplies);
      }
    } catch (error) {
      console.error('Error fetching packing supplies:', error);
      message.error('Failed to load packing supplies');
    } finally {
      setLoading(false);
    }
  };

  const addProduct = (index) => {
    const updatedSupplies = [...supplies];
    updatedSupplies[index].quantity = (updatedSupplies[index].quantity || 0) + 1;
    updatedSupplies[index].selectedQuantity = updatedSupplies[index].quantity;
    setSupplies(updatedSupplies);

    // Update Redux state immediately for real-time order summary
    const selectedSupplies = updatedSupplies.filter(s => s.quantity > 0);
    dispatch(setPackingSupplies(selectedSupplies));
  };

  const handleNext = () => {
    const selectedSupplies = supplies.filter(s => s.quantity > 0);
    dispatch(setPackingSupplies(selectedSupplies));
    // Parent handles step increment
  };

  if (loading) {
    return (
      <div>
        <div className="steps bg-white">
          <h2 className="shopping-cart-header text-white text-center py-2 fs-20 steps-title">
            Step 2: Packing Supplies
          </h2>
          <div className="container px-5 my-4">
            <Skeleton active />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="steps bg-white" id="steps">
        <h2 className="shopping-cart-header text-white text-center py-2 fs-20 steps-title">
          Step 2: Packing Supplies
        </h2>
        <div className="row pl-2 pr-2">
          <div className="container px-5 my-4">
            <div className="row py-4 pt-0">
              {supplies.map((obj, index) => (
                <div key={obj.id} className="col-md-4 p-0 pkg d-flex mb-4 z-index-0">
                  <div className="card w-100">
                    <div className="product-header">
                      <p className="text-center mb-0 product-title color-primary fw-700">
                        {obj.title}
                      </p>
                    </div>

                    <div className="card-body">
                      <div className="product-image text-center">
                        <img className="img-fluid w-75 h-100" src={obj.image} alt={obj.title} />
                      </div>
                      <div className="product-info text-center pl-2 pr-2">
                        <div className="mt-3">
                          <div className="text-left ml-4 mr-3">
                            {obj.description.map((desc, descIndex) => (
                              <p className="mb-0 fs-14" key={descIndex} hidden={!desc.length}>
                                <i className="fas fa-check color-primary"></i> {desc}
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="cardfooter mb-3">
                      <div className="product-price mb-2 mt-2 color-primary font-bold">
                        <p className="text-center mb-0 pt-2 pb-2 fs-20 fw-600">
                          ${obj.price.toFixed(2)}
                        </p>
                      </div>

                      <div className="text-center mb-3">
                        {obj?.selectedQuantity ? (
                          <button
                            className="btn product-added-btn br-5 fs-15 width-90p border-0 text-center position-relative red-border-button"
                            onClick={() => addProduct(index)}
                          >
                            <b>+ Add</b>
                            <button className="circle">
                              <p className="mb-0 pb-0">{obj?.selectedQuantity}</p>
                            </button>
                          </button>
                        ) : (
                          <button
                            className="btn btn-danger br-5 fs-15 width-90p border-0 bg-theme-secondary text-center red-button"
                            onClick={() => addProduct(index)}
                          >
                            <b>+ Add</b>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

PackingSupplies.propTypes = {
  isParentNextStepClicked: PropTypes.bool,
  setParentNextStepClicked: PropTypes.func,
  tabref: PropTypes.object,
};

export default PackingSupplies;
